import { RecordAfterFact } from "@/ui/RecordAfterFact";
import { ObjectReadGuard } from "@/ui/ObjectReadGuard";
import { useEffect, useState } from "react";
import { packetReadinessCheck, panelAnswerQuestion, panelListQuestions } from "@/api/tools";
import { useRead, useWrite } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import { Pill, ReasonDialog, fmtDate } from "@/ui";
import { KickoffPackets } from "@/views/panel/KickoffPackets";

// Route: `#requisitions/<R>/packets`. The page is Kickoff & Packets (V2, lane B6 S12): the packet chain, the coverage
// validator, the Recruiter's review and release, and the panel acknowledgment summary, built in views/panel. Panel
// changes go through the panel-change request (S13), never a direct swap. The panel's questions stay here until their
// own screen moves (V1-INTERACTIONS §4.10).

export function PacketSetPage({ id }: { id: string }) {
  const { register } = useViewState();
  const { can } = useSession();
  const q = useRead(["packet-set", id], () => packetReadinessCheck({ requisition_id: id }));
  const questions = useRead(["questions", id], () => panelListQuestions({ requisition_id: id }), { enabled: can("panel.list_questions") });
  useEffect(() => register({ view: "packets", ids: { requisition: id }, summary: `packet set ${q.data?.set_status ?? ""}` }), [register, id, q.data?.set_status]);
  const answer = useWrite((i: { requisition_id: string; question_id: string; answer: string }, k) => panelAnswerQuestion(i, k), [["questions", id], ["today"]]);
  const [dlg, setDlg] = useState<null | { qid: string; question: string }>(null);
  if (q.error || !q.data) return <ObjectReadGuard query={q} />;
  return (
    <div>
      <RecordAfterFact id={id} family="requisition" section="packets" />
      <KickoffPackets requisitionId={id} />
      {questions.data && questions.data.items.length > 0 && (
        <section className="card">
          <h2 className="sec">
            Questions from the panel <span className="sec__n">{questions.data.items.length}</span>
          </h2>
          <ul className="history">
            {questions.data.items.map((x) => (
              <li key={x.id}>
                <b>{x.asker}</b> {x.round_label ? `· ${x.round_label}` : ""} · {fmtDate(x.asked_at, true)}
                <div>{x.question}</div>
                {x.answer ? (
                  <div className="muted">
                    Answer ({x.answered_by}, {fmtDate(x.answered_at, true)}): {x.answer}
                  </div>
                ) : can("panel.answer_question") ? (
                  <button className="btn btn--sm btn--primary" onClick={() => setDlg({ qid: x.id, question: x.question })}>
                    Answer
                  </button>
                ) : (
                  <Pill tone="warn">unanswered</Pill>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      <ReasonDialog title="Answer the question" label="Answer" confirm="Send answer" open={!!dlg} onClose={() => setDlg(null)} busy={answer.isPending} error={answer.error} onConfirm={(text) => dlg && void answer.mutateAsync({ requisition_id: id, question_id: dlg.qid, answer: text }).then(() => setDlg(null))}>
        {dlg && <p className="rule">{dlg.question}</p>}
      </ReasonDialog>
    </div>
  );
}
